import { ROW_FLASH_DURATION } from '@frontend/js/constants';

/**
 * Function to Initialize uninitialized tooltips.
 *
 * @param {Element} element
 */
export function initTooltips(element) {
    const newTooltipsList = [].slice.call(
        element.querySelectorAll('[data-bs-toggle="tooltip"]')
    );

    let options = {};

    newTooltipsList.map(function (el) {
        if (el.getAttribute('data-kt-initialized') === '1') {
            return;
        }

        var delay = {};

        // Handle delay options
        if (el.hasAttribute('data-bs-delay-hide')) {
            delay['hide'] = el.getAttribute('data-bs-delay-hide');
        }

        if (el.hasAttribute('data-bs-delay-show')) {
            delay['show'] = el.getAttribute('data-bs-delay-show');
        }

        if (delay) {
            options['delay'] = delay;
        }

        // Check dismiss options
        if (
            el.hasAttribute('data-bs-dismiss') &&
            el.getAttribute('data-bs-dismiss') == 'click'
        ) {
            options['dismiss'] = 'click';
        }

        // Initialize popover
        var tp = new bootstrap.Tooltip(el, options);

        // Handle dismiss
        if (options['dismiss'] && options['dismiss'] === 'click') {
            // Hide popover on element click
            el.addEventListener('click', function (e) {
                tp.hide();
            });
        }

        el.setAttribute('data-kt-initialized', '1');

        return tp;
    });
}

let isPopoverCloseDelegated = false;

/**
 * Function to Initialize uninitialized popovers.
 *
 * @param {Element} element
 */
export function initPopovers(element) {
    const newPopoversList = [].slice.call(
        element.querySelectorAll('[data-bs-toggle="popover"]')
    );

    delegatePopoverClose();

    newPopoversList.map(function (el) {
        if (el.getAttribute('data-kt-initialized') === '1') {
            return;
        }

        // Rendered on the body so a scrollable or overflow-hidden ancestor cannot clip it.
        const popover = new bootstrap.Popover(el, { container: 'body' });

        el.setAttribute('data-kt-initialized', '1');

        return popover;
    });
}

/**
 * Close a popover from a .popover-close control inside its content.
 *
 * Delegated from the document, because Bootstrap rebuilds the popover element on every show and
 * the content is sanitised on the way in, so there is no stable node to bind to up front.
 */
function delegatePopoverClose() {
    if (isPopoverCloseDelegated) {
        return;
    }

    isPopoverCloseDelegated = true;

    document.addEventListener('click', function (event) {
        if (!(event.target instanceof Element)) {
            return;
        }

        const closeEl = event.target.closest('.popover-close');

        if (!closeEl) {
            return;
        }

        hidePopoverOf(closeEl.closest('.popover'));
    });

    document.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') {
            return;
        }

        document.querySelectorAll('.popover').forEach(hidePopoverOf);
    });
}

/**
 * Hide the popover a rendered popover element belongs to.
 *
 * @param {Element|null} tip
 */
function hidePopoverOf(tip) {
    // An id-less element would make the lookup below match unrelated nodes.
    if (!tip || !tip.id) {
        return;
    }

    const trigger = document.querySelector('[aria-describedby="' + tip.id + '"]');
    const popover = trigger ? bootstrap.Popover.getInstance(trigger) : null;

    if (popover) {
        popover.hide();
    }
}

/**
 * Function to focus an input element.
 *
 * @param {HTMLElement} element
 */
export function focusInput(element) {
    const input = element?.querySelector('input[type=text]');
    const endPosition = input.value.length;

    input.setSelectionRange(endPosition, endPosition);
    input.focus();
}

/**
 * Function to flash background color of a table row.
 *
 * @param {HTMLTableRowElement} row
 */
export function flashRow(row, duration = ROW_FLASH_DURATION,color = 'lightgreen') {
    row.style.backgroundColor = color;
    row.style.opacity = '0';
    row.style.transition = 'background-color 0.5s, opacity 1s';

    // Trigger reflow to enable the transition
    void row.offsetWidth;

    // Fade in
    row.style.opacity = '1';

    setTimeout(function () {
        row.style.backgroundColor = '';
    }, duration);
}

export function flashRowById(id) {
    const row = document.querySelector(`tr[data-row="${id}"]`);

    if (row) {
        flashRow(row);
    }
}

/**
 * Function to make button to in progress mode.
 *
 * @param {string | HTMLButtonElement} button - The button selector or element.
 */
export function makeBtnToLoading(button) {
    let btn;

    if (typeof button === 'string') {
        btn = document.querySelector(button);
    } else if (button instanceof HTMLButtonElement) {
        btn = button;
    }

    if (btn) {
        btn.setAttribute('data-kt-indicator', 'on');
        btn.disabled = true;
    }
}

/**
 * Function to show success toast.
 *
 * @param {string} message
 */
export function showSuccessToast(message) {
    toastr.success(message);
}

export function showWarningToast(message) {
    toastr.warning(message);
}


/**
 * Function to show errors from backend.
 *
 * @param {Object} errorData
 * @param {HTMLElement} element
 */
export function handleApiErrors(element, errorResponse) {
    if (errorResponse?.status === 422) {
        const errorData = errorResponse?.data?.errors;

        for (let field in errorData) {
            const message = errorData[field][0];
            element
                .querySelectorAll(`.api-error[data-for="${field}"]`)
                .forEach((element) => {
                    if (element) {
                        element.innerText = message;
                        element.classList.remove('d-none');
                    }
                });
        }
    } else {
        showErrorToast(errorResponse?.data?.message ?? 'Something unexpected happened.');
    }
}

/**
 * Function to reset all api error element
 */
export function resetApiErrors() {
    document
        .querySelectorAll('.api-error')
        .forEach((element) => element.classList.add('d-none'));
}

/**
 * Function to show error toast.
 *
 * @param {string} message
 */
export function showErrorToast(message) {
    toastr.error(message);
}

/**
 * Function to get the current locale.
 */
export function getAppLocale() {
    return document.getElementsByTagName('html')[0]?.getAttribute('lang');
}

/**
 * Function to check the current locale.
 *
 * @param {String} locale
 */
export function isLocale(locale) {
    return getAppLocale() == locale;
}

/**
 * Function to check if current locale is english.
 */
export function isEn() {
    return isLocale('en');
}

/**
 * Function to hide loading status of elements.
 */
export function hideLoadingIndicators(removeDisabledState = true) {
    document.querySelectorAll('[data-kt-indicator]').forEach((element) => {
        element.removeAttribute('data-kt-indicator');
        if (removeDisabledState) {
            element.disabled = false;
        }
    });
}

/**
 * Function to Initialize uninitialized select2.
 *
 * @param {Element} element
 */
export function initSelect2(element) {
    const elements = [].slice.call(
        element.querySelectorAll(
            '[data-control="select2"], [data-kt-select2="true"]'
        )
    );

    const options = {
        dir: document.body.getAttribute('direction'),
    };

    elements.map(function (element) {
        if (element.getAttribute('data-kt-initialized') === '1') {
            return;
        }

        if (element.getAttribute('data-hide-search') == 'true') {
            options.minimumResultsForSearch = Infinity;
        }

        $(element).select2(options);

        // Handle Select2's KTMenu parent case
        if (
            element.hasAttribute('data-dropdown-parent') &&
            element.hasAttribute('multiple')
        ) {
            var parentEl = document.querySelector(
                element.getAttribute('data-dropdown-parent')
            );

            if (parentEl && parentEl.hasAttribute('data-kt-menu')) {
                var menu = new KTMenu(parentEl);

                if (menu) {
                    $(element).on('select2:unselect', function (e) {
                        element.setAttribute('data-multiple-unselect', '1');
                    });

                    menu.on('kt.menu.dropdown.hide', function (item) {
                        if (
                            element.getAttribute('data-multiple-unselect') ===
                            '1'
                        ) {
                            element.removeAttribute('data-multiple-unselect');
                            return false;
                        }
                    });
                }
            }
        }

        element.setAttribute('data-kt-initialized', '1');
    });
}

export function showThumbnailOnHover() {
    const thumbnailElements = document.querySelectorAll('.hover-thumbnail');

    thumbnailElements.forEach((element) => {
        let previewImg;

        element.addEventListener('mouseenter', () => {
            const imgUrl = element.getAttribute('data-image');
            if (!imgUrl) return;

            previewImg = document.createElement('img');
            previewImg.src = imgUrl;
            previewImg.className = 'hover-thumbnail-preview';
            previewImg.style.visibility = 'hidden';

            document.body.appendChild(previewImg);

            previewImg.onload = () => {

                // Check if previewImg is still in the DOM - it might have been removed if the user quickly moves the mouse away
                if (!previewImg || !document.body.contains(previewImg)) {
                    return;
                }

                const rect = element.getBoundingClientRect();
                const offset = 5;

                const imgHeight = previewImg?.offsetHeight;
                const spaceBelow = window.innerHeight - rect.bottom;
                const spaceAbove = rect.top;

                if (spaceBelow >= imgHeight || spaceBelow >= spaceAbove) {
                    previewImg.style.top = rect.bottom + window.scrollY + offset + 'px';
                } else {
                    previewImg.style.top = rect.top + window.scrollY - imgHeight - offset + 'px';
                }

                previewImg.style.left = (rect.left + rect.width/2 + offset) + window.scrollX + 'px';
                previewImg.style.visibility = 'visible';
            };
        });

        element.addEventListener('mouseleave', () => {
            if (previewImg && previewImg.parentNode) {
                previewImg.parentNode.removeChild(previewImg);
                previewImg = null;
            }
        });
    });
}

export function getElementByName(name, parent) {
    return (parent ?? document).querySelector(`[name="${name}"]`);
}

export function setElementValueByName(name, value, parent = null) {
    (parent ?? document).querySelector(`[name="${name}"]`).value = value;
}

export function getElementByDataId(name, parent) {
    return (parent ?? document).querySelector(`[data-id="${name}"]`);
}

export function setElementContentByDataId(name, value, parent = null) {
    if ((parent ?? document).querySelector(`[data-id="${name}"]`)) {
        (parent ?? document).querySelector(`[data-id="${name}"]`).textContent = value;
    }
}

export function getAllElementsByDataId(name, parent) {
    return (parent ?? document).querySelectorAll(`[data-id="${name}"]`);
}

export function findKeyByValue(obj, value) {
    return Object.keys(obj).find((key) => obj[key] == value);
}

export function getTableRowById(id, parent) {
    const tbody = document.querySelector(
        'table tbody'
    );
    return (parent ?? tbody)?.querySelector(`[data-row="${id}"]`);
}


/**
 * Remove parameter from url.
 */
export function removeParamFromUrl(param) {
    const url = new URL(window.location.href);
    if (url.searchParams.has(param)) {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete(param);

        const newUrl =
            window.location.origin +
            window.location.pathname +
            '?' +
            searchParams.toString();
        history.replaceState({}, document.title, newUrl);
    }
}

/**
 * Hide the breadcrumb back button.
 */
export function hideBreadcrumbBackBtn() {
    removeParamFromUrl('back')
}

/**
 * Set focus to an HTML element
 */
export function setFocus(elementId) {
    document.getElementById(elementId)?.focus();
}

